import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../../lib/api.js';

function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function truncate(str, n) {
  if (!str) return '—';
  return str.length > n ? str.slice(0, n) + '…' : str;
}

function RunStatusBadge({ status }) {
  const cls =
    status === 'succeeded'
      ? 'bg-hxd-ok'
      : status === 'failed'
      ? 'bg-hxd-err'
      : status === 'running'
      ? 'bg-hxd-warn'
      : status === 'cancelled'
      ? 'bg-hxd-subtle'
      : 'bg-hxd-muted';
  return (
    <span
      className={`${cls} text-white font-display text-[9px] font-bold uppercase tracking-display-sm px-1.5 py-0.5 rounded-[2px]`}
    >
      {status}
    </span>
  );
}

function Banner({ type, message, detail, onDismiss }) {
  const styles = {
    success: 'bg-green-50 border-green-200 text-green-800',
    warning: 'bg-orange-50 border-orange-200 text-orange-800',
    error: 'bg-red-50 border-red-200 text-hxd-err',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
  };
  return (
    <div
      className={`flex items-start justify-between gap-3 border rounded p-3 ${styles[type] ?? styles.info}`}
    >
      <div className="min-w-0">
        <p className="text-[13px]">{message}</p>
        {detail && <p className="text-[12px] mt-1 opacity-75 break-words">{detail}</p>}
      </div>
      <button
        onClick={onDismiss}
        className="opacity-50 hover:opacity-100 text-lg leading-none flex-shrink-0"
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
}

export default function Runs() {
  const [latestRun, setLatestRun] = useState(undefined); // undefined = not yet loaded
  const [runs, setRuns] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [banner, setBanner] = useState(null); // { type, message, detail }

  const fetchLatest = useCallback(async () => {
    try {
      const data = await api.get('/dev/pipeline/runs/latest');
      setLatestRun(data);
    } catch (err) {
      if (err.status === 404) {
        setLatestRun(null);
      }
      // other errors: leave latestRun as-is
    }
  }, []);

  const fetchPage = useCallback(async (cursor = null, replace = false) => {
    const qs = cursor ? `?limit=20&cursor=${cursor}` : '?limit=20';
    const data = await api.get(`/dev/pipeline/runs${qs}`);
    if (replace) {
      setRuns(data.runs);
    } else {
      setRuns((prev) => [...prev, ...data.runs]);
    }
    setNextCursor(data.nextCursor);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([fetchLatest(), fetchPage(null, true)]);
    } catch {
      // individual fetches handle their own errors
    } finally {
      setLoading(false);
    }
  }, [fetchLatest, fetchPage]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleLoadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      await fetchPage(nextCursor, false);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleTrigger = async () => {
    setTriggering(true);
    setBanner(null);
    try {
      const data = await api.post('/dev/pipeline/run');
      // 202 success
      setBanner({ type: 'success', message: `Run queued: ${data.runId}` });
    } catch (err) {
      if (err.status === 409) {
        setBanner({
          type: 'warning',
          message: `Already in progress: ${err.data?.runId ?? ''}`,
        });
      } else if (err.status === 502) {
        setBanner({
          type: 'error',
          message: `VM unreachable. Run marked failed: ${err.data?.runId ?? ''}`,
          detail: err.data?.detail,
        });
      } else if (err.status === 503) {
        setBanner({ type: 'error', message: 'Pipeline VM not configured.' });
      } else {
        setBanner({ type: 'error', message: err.message });
      }
    } finally {
      setTriggering(false);
      // Refetch both regardless of outcome
      await Promise.all([fetchLatest(), fetchPage(null, true)]);
    }
  };

  const inProgress =
    latestRun?.status === 'queued' || latestRun?.status === 'running';

  return (
    <div className="max-w-page mx-auto px-4 sm:px-6 py-8">
      <Link
        to="/admin/dev"
        className="text-[12px] text-hxd-muted hover:text-hxd-orange transition-colors"
      >
        ← Back to Dev Dashboard
      </Link>
      <div className="mb-6 mt-4">
        <h1 className="hxd-page-title">Pipeline Runs</h1>
        <p className="hxd-page-subtitle">
          Trigger the ingestion pipeline and view run history.
        </p>
      </div>

      {/* Banner */}
      {banner && (
        <div className="mb-5">
          <Banner {...banner} onDismiss={() => setBanner(null)} />
        </div>
      )}

      {/* Action card */}
      <div className="hxd-card mb-6">
        <div className="hxd-card-header">Trigger</div>
        <div className="flex items-center gap-4 flex-wrap">
          <button
            className="hxd-btn hxd-btn-primary"
            onClick={handleTrigger}
            disabled={triggering || inProgress}
          >
            {triggering ? 'Triggering…' : 'Run pipeline now'}
          </button>
          {inProgress && (
            <p className="text-hxd-muted text-[13px]">A run is already in progress.</p>
          )}
        </div>
        {latestRun?.status === 'failed' && latestRun.errorMessage && (
          <p className="text-hxd-err text-[12px] mt-3">
            Last run failed: {latestRun.errorMessage}
          </p>
        )}
      </div>

      {/* Run history table */}
      {loading ? (
        <p className="text-hxd-muted text-[13px]">Loading runs…</p>
      ) : runs.length === 0 ? (
        <div className="hxd-card">
          <p className="text-hxd-muted text-[13px]">
            No runs yet. Click <strong>Run pipeline now</strong> to trigger the first one.
          </p>
        </div>
      ) : (
        <>
          <div className="hxd-card p-0 overflow-x-auto">
            <table className="w-full text-[13px] min-w-[580px]">
              <thead>
                <tr className="border-b-2 border-hxd-orange bg-hxd-surface">
                  {['Status', 'Requested At', 'Brief ID', 'Error', 'Actions'].map((h) => (
                    <th
                      key={h}
                      className="font-display text-[10px] font-bold tracking-display-md uppercase text-left px-4 py-3"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr
                    key={r._id}
                    className="border-b border-hxd-border last:border-0 hover:bg-hxd-surface transition-colors"
                  >
                    <td className="px-4 py-3">
                      <RunStatusBadge status={r.status} />
                    </td>
                    <td className="px-4 py-3 text-hxd-muted whitespace-nowrap">
                      {fmt(r.requestedAt)}
                    </td>
                    <td className="px-4 py-3 text-hxd-muted font-mono text-[12px]">
                      {r.briefId ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-hxd-muted max-w-[200px]">
                      {r.status === 'failed' ? truncate(r.errorMessage, 60) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/dev/runs/${r._id}`}
                        className="hxd-btn hxd-btn-ghost hxd-btn-sm"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {nextCursor && (
            <div className="mt-4 text-center">
              <button
                className="hxd-btn hxd-btn-ghost hxd-btn-sm"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
